import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover } from "antd";
import { Icon, IconName } from "Components/UI";
import { defaultDisciplineIcons, genericPickerIcons, iconSearchTerms } from "Utils/Icons";
import {
  EmptyResult,
  GroupsContainer,
  IconGroup,
  IconOption,
  IconsGrid,
  PopoverContent,
  SearchBox,
  TriggerButton,
} from "./styles";

interface IDisciplineIconPicker {
  value: IconName;
  disabled?: boolean;
  onChange: (icon: IconName) => void;
}

const humanizeIconName = (icon: string) => icon
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^./, (char) => char.toUpperCase());

const normalize = (text: string) => text
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim();

const matchesSearch = (icon: IconName, search: string) => {
  if (!search) return true;

  const normalizedSearch = normalize(search);
  const searchableTerms = [humanizeIconName(icon), ...(iconSearchTerms[icon] || [])];

  return searchableTerms.some((term) => normalize(term).includes(normalizedSearch));
};

const DisciplineIconPicker: FC<IDisciplineIconPicker> = ({
  value,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation('editObra');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredDisciplineIcons = useMemo(() => (
    defaultDisciplineIcons.filter((icon) => matchesSearch(icon, search))
  ), [search]);

  const filteredGenericIcons = useMemo(() => (
    genericPickerIcons.filter((icon) => matchesSearch(icon, search))
  ), [search]);

  const hasResults = filteredDisciplineIcons.length > 0 || filteredGenericIcons.length > 0;

  const handleSelect = (icon: IconName) => {
    onChange(icon);
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearch('');
    }
  };

  const content = (
    <PopoverContent>
      <SearchBox>
        <Icon icon="buscar" customSize={16} className="searchIcon" />
        <input
          type="text"
          className="searchField"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('disciplines.iconSearchPlaceholder')}
        />
      </SearchBox>

      <GroupsContainer>
        {filteredDisciplineIcons.length > 0 && (
          <IconGroup>
            <span className="groupTitle">{t('disciplines.iconGroupDisciplines')}</span>
            <IconsGrid>
              {filteredDisciplineIcons.map((icon) => (
                <IconOption
                  key={icon}
                  type="button"
                  title={humanizeIconName(icon)}
                  selected={value === icon}
                  onClick={() => handleSelect(icon)}
                >
                  <Icon icon={icon} customSize={16} className="icon" />
                </IconOption>
              ))}
            </IconsGrid>
          </IconGroup>
        )}

        {filteredGenericIcons.length > 0 && (
          <IconGroup>
            <span className="groupTitle">{t('disciplines.iconGroupIcons')}</span>
            <IconsGrid>
              {filteredGenericIcons.map((icon) => (
                <IconOption
                  key={icon}
                  type="button"
                  title={humanizeIconName(icon)}
                  selected={value === icon}
                  onClick={() => handleSelect(icon)}
                >
                  <Icon icon={icon} customSize={16} className="icon" />
                </IconOption>
              ))}
            </IconsGrid>
          </IconGroup>
        )}

        {!hasResults && (
          <EmptyResult>{t('disciplines.iconNoResults')}</EmptyResult>
        )}
      </GroupsContainer>
    </PopoverContent>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomLeft"
      open={open}
      onOpenChange={disabled ? undefined : handleOpenChange}
    >
      <TriggerButton type="button" disabled={disabled}>
        <Icon icon={value} customSize={16} className="selectedIcon" />
        <Icon icon="expandir" customSize={12} className="chevronIcon" />
      </TriggerButton>
    </Popover>
  );
};

export default DisciplineIconPicker;
